import { 
  getViagemById, 
  createViagem, 
  updateViagem,
  getVeiculos,
  getMotoristas,
  getEstados,
  getCidades
} from "@spark/services";
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { formStructure, formSections, validateForm } from '../Cadastro/components/Forms/formConfig';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import { notification } from '@spark/ui-components/utils/notification';

// Função auxiliar para extrair valor e ID
const extractValue = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && value.value !== undefined) {
    const parsed = Number(value.value);
    return isNaN(parsed) ? value.value : parsed;
  }
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return isNaN(parsed) ? value : parsed;
};

const mapFormToCreateApi = (values) => {
  // Função para formatar datas para o padrão MySQL
  const formatarDataParaAPI = (data) => {
    if (!data) return null;

    if (typeof data === 'string' && data.length === 10) {
      // Já está no formato YYYY-MM-DD
      return data + ' 00:00:00';
    }

    try {
      const d = new Date(data);
      if (isNaN(d.getTime())) return null;

      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  // Extrair IDs dos campos SearchSelect
  const idUfOrigem = extractValue(values.IdUfOrigem);
  const idUfDestino = extractValue(values.IdUfDestino);
  const idCidadeOrigem = extractValue(values.IdCidadeOrigem);
  const idCidadeDestino = extractValue(values.IdCidadeDestino);

  return {
    CodigoIntegracao: values.CodigoIntegracao || null,
    IdCarga: values.IdCarga ? parseInt(values.IdCarga) : null,
    NumeroCarga: values.NumeroCarga || null,
    IdVeiculo: values.IdVeiculo ? parseInt(values.IdVeiculo) : null,
    NumeroPlaca: values.NumeroPlaca || '',
    NumeroFrota: values.NumeroFrota || null,
    IdMotorista: values.IdMotorista ? parseInt(values.IdMotorista) : null,
    NomeMotorista: values.NomeMotorista || '',
    IdUfOrigem: idUfOrigem ? parseInt(idUfOrigem) : null,
    IdCidadeOrigem: idCidadeOrigem ? parseInt(idCidadeOrigem) : null,
    IdUfDestino: idUfDestino ? parseInt(idUfDestino) : null,
    IdCidadeDestino: idCidadeDestino ? parseInt(idCidadeDestino) : null,
    DataInicio: formatarDataParaAPI(values.DataInicio),
    DataFim: formatarDataParaAPI(values.DataFim),
    TotalKmRodado: values.TotalKmRodado ? parseFloat(values.TotalKmRodado) : null,
    ValorFreteLiquido: values.ValorFreteLiquido ? parseFloat(values.ValorFreteLiquido) : null,
    ValorFreteBruto: values.ValorFreteBruto ? parseFloat(values.ValorFreteBruto) : null,
    NotasFiscais: values.NotasFiscais || null
  };
};

const ViagemForm = ({
  viagemId,
  isViewMode = false,
  onSuccess,
  onCancel,
  showButtons = true,
  showCard = true,
  initialDataFromParent = null,
  disabled = false
}) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const formValuesRef = useRef({});

  // Usar viagemId se fornecido, senão usar o id da URL
  const finalViagemId = viagemId || id;
  const isEditing = !!finalViagemId;

  // Carregar dados da viagem se estiver editando
  useEffect(() => {
    if (!isEditing) return;

    // Se dados já vierem do pai, usar sem refetch
    if (initialDataFromParent) {
      setInitialData({
        CodigoIntegracao: initialDataFromParent.CodigoIntegracao || '',
        IdCarga: initialDataFromParent.IdCarga || '',
        NumeroCarga: initialDataFromParent.NumeroCarga || '',
        IdVeiculo: initialDataFromParent.IdVeiculo || '',
        NumeroPlaca: initialDataFromParent.NumeroPlaca || '',
        NumeroFrota: initialDataFromParent.NumeroFrota || '',
        IdMotorista: initialDataFromParent.IdMotorista || '',
        NomeMotorista: initialDataFromParent.NomeMotorista || '',
        IdUfOrigem: initialDataFromParent.IdUfOrigem || '',
        IdCidadeOrigem: initialDataFromParent.IdCidadeOrigem || '',
        IdUfDestino: initialDataFromParent.IdUfDestino || '',
        IdCidadeDestino: initialDataFromParent.IdCidadeDestino || '',
        DataInicio: initialDataFromParent.DataInicio ? initialDataFromParent.DataInicio.split('T')[0] : '',
        DataFim: initialDataFromParent.DataFim ? initialDataFromParent.DataFim.split('T')[0] : '',
        TotalKmRodado: initialDataFromParent.TotalKmRodado || '',
        ValorFreteLiquido: initialDataFromParent.ValorFreteLiquido || '',
        ValorFreteBruto: initialDataFromParent.ValorFreteBruto || '',
        NotasFiscais: initialDataFromParent.NotasFiscais || ''
      });
      return;
    }

    if (finalViagemId) {
      setFormLoading(true);
      loadViagem();
    }
  }, [isEditing, finalViagemId, initialDataFromParent]);

  const loadViagem = async () => {
    try {
      const response = await getViagemById(finalViagemId);
      const viagem = response?.data?.data || response;

      if (viagem) {
        // Converter IDs para formato SearchSelect {value, label}
        const ufOrigemValue = viagem.IdUfOrigem ? {
          value: String(viagem.IdUfOrigem),
          label: viagem.NomeEstadoOrigem ? `${viagem.NomeEstadoOrigem} (${viagem.SiglaEstadoOrigem})` : `UF ${viagem.IdUfOrigem}`
        } : '';

        const ufDestinoValue = viagem.IdUfDestino ? {
          value: String(viagem.IdUfDestino),
          label: viagem.NomeEstadoDestino ? `${viagem.NomeEstadoDestino} (${viagem.SiglaEstadoDestino})` : `UF ${viagem.IdUfDestino}`
        } : '';

        const cidadeOrigemValue = viagem.IdCidadeOrigem ? {
          value: String(viagem.IdCidadeOrigem),
          label: viagem.NomeCidadeOrigem || `Cidade ${viagem.IdCidadeOrigem}`
        } : '';

        const cidadeDestinoValue = viagem.IdCidadeDestino ? {
          value: String(viagem.IdCidadeDestino),
          label: viagem.NomeCidadeDestino || `Cidade ${viagem.IdCidadeDestino}`
        } : '';

        setInitialData({
          CodigoIntegracao: viagem.CodigoIntegracao || '',
          IdCarga: viagem.IdCarga || '',
          NumeroCarga: viagem.NumeroCarga || '',
          IdVeiculo: viagem.IdVeiculo || '',
          NumeroPlaca: viagem.NumeroPlaca || '',
          NumeroFrota: viagem.NumeroFrota || '',
          IdMotorista: viagem.IdMotorista || '',
          NomeMotorista: viagem.NomeMotorista || '',
          IdUfOrigem: ufOrigemValue,
          IdCidadeOrigem: cidadeOrigemValue,
          IdUfDestino: ufDestinoValue,
          IdCidadeDestino: cidadeDestinoValue,
          DataInicio: viagem.DataInicio ? viagem.DataInicio.split('T')[0] : '',
          DataFim: viagem.DataFim ? viagem.DataFim.split('T')[0] : '',
          TotalKmRodado: viagem.TotalKmRodado || '',
          ValorFreteLiquido: viagem.ValorFreteLiquido || '',
          ValorFreteBruto: viagem.ValorFreteBruto || '',
          NotasFiscais: viagem.NotasFiscais || ''
        });
      }
    } catch (error) {
      console.error('❌ Erro ao carregar viagem:', error);
      // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      setFormLoading(false);
    }
  };

  const onSubmit = useCallback(
    async (values) => {
      try {
        setLoading(true);
        const payload = mapFormToCreateApi(values);

        if (isEditing) {
          await updateViagem(finalViagemId, payload);
          notification.success('Viagem atualizada com sucesso!');
        } else {
          await createViagem(payload);
          notification.success('Viagem criada com sucesso!');
        }

        // Callback personalizado ou navegação padrão
        if (onSuccess) {
          onSuccess();
        } else {
          history.goBack();
        }
      } catch (err) {
        console.error('❌ Erro ao salvar viagem:', err);
        notification.error(err.message || 'Erro ao salvar viagem', `Erro ao ${isEditing ? 'atualizar' : 'criar'} viagem`);
      } finally {
        setLoading(false);
      }
    },
    [history, finalViagemId, isEditing, onSuccess]
  );

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      history.goBack();
    }
  }, [onCancel, history]);

  // Função para buscar veículos por placa
  const handleBuscarVeiculos = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      return [];
    }

    try {
      const response = await getVeiculos({
        placa: searchTerm,
        quantidadePorPagina: 10
      });

      const responseData = response?.data || response;
      const registros = responseData?.registros || responseData?.Registros || [];
      return registros.map((item) => {
        const placa = item.Placa || item.placa || '';
        const frota = item.Frota || item.frota || '';
        const idVeiculo = item.Id || item.id || item.IdVeiculo || item.idVeiculo;

        return {
          value: placa,
          label: `${placa}${frota ? ` - Frota: ${frota}` : ''}`,
          data: {
            ...item,
            IdVeiculo: idVeiculo,
            Veiculo: placa,
            Frota: frota
          }
        };
      });
    } catch (error) {
      console.error('Erro ao buscar veículos:', error);
      return [];
    }
  }, []);

  // Função para buscar motoristas por nome
  const handleBuscarMotoristas = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      return [];
    }

    try {
      const response = await getMotoristas({
        Nome: searchTerm,
        quantidadePorPagina: 10
      });

      const responseData = response?.data || response;
      const registros = responseData?.registros || responseData?.Registros || [];
      return registros.map((item) => {
        const nome = item.Nome || item.nome || '';
        const idMotorista = item.Id || item.id || item.IdMotorista || item.idMotorista;

        return {
          value: nome,
          label: nome,
          data: {
            ...item,
            IdMotorista: idMotorista,
            NomeMotorista: nome
          }
        };
      });
    } catch (error) {
      console.error('Erro ao buscar motoristas:', error);
      return [];
    }
  }, []);

  // Função para buscar UFs
  const searchEstados = useCallback(async (searchTerm) => {
    try {
      const response = await getEstados({ Nome: searchTerm, quantidadePorPagina: 30 });
      const responseData = response?.data || response;
      const registros = responseData?.registros || responseData?.Registros || [];

      return registros.map((item) => ({
        value: String(item.Id || item.id),
        label: `${item.Nome || item.nome} (${item.Uf || item.uf})`,
      }));
    } catch (error) {
      console.error('Erro ao buscar estados:', error);
      return [];
    }
  }, []);

  // Função para buscar cidades de origem
  const searchCidadesOrigem = useCallback(async (searchTerm) => {
    try {
      const filters = { Nome: searchTerm, quantidadePorPagina: 30 };

      // Filtrar por UF se estiver selecionada
      const idUfOrigem = extractValue(formValuesRef.current?.IdUfOrigem);
      if (idUfOrigem) {
        filters.IdEstado = parseInt(idUfOrigem, 10);
      }

      const response = await getCidades(filters);
      const responseData = response?.data || response;
      const registros = responseData?.registros || responseData?.Registros || [];

      return registros.map((item) => ({
        value: String(item.Id || item.id),
        label: item.Nome || item.nome || `Cidade ${item.Id || item.id}`,
      }));
    } catch (error) {
      console.error('Erro ao buscar cidades de origem:', error);
      return [];
    }
  }, []);

  // Função para buscar cidades de destino
  const searchCidadesDestino = useCallback(async (searchTerm) => {
    try {
      const filters = { Nome: searchTerm, quantidadePorPagina: 30 };

      // Filtrar por UF se estiver selecionada
      const idUfDestino = extractValue(formValuesRef.current?.IdUfDestino);
      if (idUfDestino) {
        filters.IdEstado = parseInt(idUfDestino, 10);
      }

      const response = await getCidades(filters);
      const responseData = response?.data || response;
      const registros = responseData?.registros || responseData?.Registros || [];

      return registros.map((item) => ({
        value: String(item.Id || item.id),
        label: item.Nome || item.nome || `Cidade ${item.Id || item.id}`,
      }));
    } catch (error) {
      console.error('Erro ao buscar cidades de destino:', error);
      return [];
    }
  }, []);

  // Se está carregando os dados iniciais
  if (formLoading) {
    return (
      <Card>
        <CardBody>
          <p>Carregando dados da viagem...</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialData || {}}
      validate={validateForm}
      render={({ handleSubmit, form, submitting, pristine, values }) => {
        // Callbacks para preencher os campos automaticamente
        const handleVeiculoChange = useCallback((selectedVeiculo) => {
          if (selectedVeiculo && selectedVeiculo.data) {
            const { data } = selectedVeiculo;
            form.change('NumeroPlaca', data.Veiculo);
            form.change('NumeroFrota', data.Frota);
            form.change('IdVeiculo', data.IdVeiculo || null);
          }
        }, [form]);

        const handleMotoristaChange = useCallback((selectedMotorista) => {
          if (selectedMotorista && selectedMotorista.data) {
            const { data } = selectedMotorista;
            form.change('NomeMotorista', data.NomeMotorista);
            form.change('IdMotorista', data.IdMotorista || null);
          }
        }, [form]);

        // Atualizar ref com valores atuais do formulário
        formValuesRef.current = values;

        return (
          <FormContainer onSubmit={handleSubmit}>
            {showCard ? (
              <Card>
                <CardBody>
                  <DynamicForm
                    structure={formStructure}
                    sections={formSections(initialData || {})}
                    isViewMode={disabled || isViewMode}
                    searchFunctions={{
                      VeiculoSearch: handleBuscarVeiculos,
                      MotoristaSearch: handleBuscarMotoristas,
                      IdUfOrigem: searchEstados,
                      IdUfDestino: searchEstados,
                      IdCidadeOrigem: searchCidadesOrigem,
                      IdCidadeDestino: searchCidadesDestino
                    }}
                    customCallbacks={{
                      VeiculoSearch: handleVeiculoChange,
                      MotoristaSearch: handleMotoristaChange
                    }}
                  />

                  {showButtons && (
                    <FormButtonToolbar>
                      <ButtonWithLoading
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancelar
                      </ButtonWithLoading>
                      <ButtonWithLoading
                        variant="primary"
                        type="submit"
                        loading={loading}
                        disabled={disabled || isViewMode}
                      >
                        {isEditing ? 'Atualizar' : 'Criar'} Viagem
                      </ButtonWithLoading>
                    </FormButtonToolbar>
                  )}
                </CardBody>
              </Card>
            ) : (
              <>
                <DynamicForm
                  structure={formStructure}
                  sections={formSections(initialData || {})}
                  isViewMode={disabled || isViewMode}
                  searchFunctions={{
                    VeiculoSearch: handleBuscarVeiculos,
                    MotoristaSearch: handleBuscarMotoristas,
                    IdUfOrigem: searchEstados,
                    IdUfDestino: searchEstados,
                    IdCidadeOrigem: searchCidadesOrigem,
                    IdCidadeDestino: searchCidadesDestino
                  }}
                  customCallbacks={{
                    VeiculoSearch: handleVeiculoChange,
                    MotoristaSearch: handleMotoristaChange
                  }}
                />

                {showButtons && (
                  <FormButtonToolbar>
                    <ButtonWithLoading
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancelar
                    </ButtonWithLoading>
                    <ButtonWithLoading
                      variant="primary"
                      type="submit"
                      loading={loading}
                      disabled={disabled || isViewMode}
                    >
                      {isEditing ? 'Atualizar' : 'Criar'} Viagem
                    </ButtonWithLoading>
                  </FormButtonToolbar>
                )}
              </>
            )}
          </FormContainer>
        );
      }}
    />
  );
};

export default ViagemForm;
